import { fetchBanners, fetchListBanners } from 'actions/ContentActions'
import { trackClientEvent } from 'analytics/analytics'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import TourUSPContainer from 'components/OfferPage/TourV2/TourUSPContainer'
import TourV2TrendingDestinationsCarousel from 'components/OfferPage/TourV2/TourV2TrendingDestinationsCarousel'
import { getTourV2TrendingDestinations } from 'components/OfferPage/TourV2/TourV2TrendingLocationsList'
import TourSearchContainerPane, {
  TourSearchContainerPaneContent,
} from 'components/Search/TourV2/TourSearchContainerPane'
import GlobalSearchLoyaltyBurnToggle from 'components/Search/GlobalSearch/GlobalSearchLoyaltyBurnToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { TourHeroImageGravityType } from 'components/Search/TourV2/TourSearchHeroContainer'
import TourSearchHeroContainer from 'components/Search/TourV2/TourSearchHeroContainer'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import TourPageSearchCarousel from 'components/Tours/TourPageSearchCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { TOUR_ONLY_REGIONS } from 'constants/config/region'
import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GeoContext from 'contexts/geoContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import { EmptyArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import useTourV2VerticalName from 'hooks/TourV2/useTourV2VerticalName'
import styled from 'styled-components'
import ToursPageHighIntentTours from './ToursPageHighIntentTours'
import TourSpendEarnPageBanner from 'components/Tours/TourSpendEarnPageBanner'
import { isTourPromoSpendEarnEnabled } from 'selectors/TourV2/toursSelectors'
import TourCatalogueBanner from 'components/Tours/TourCatalogueBanner'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import TourHeroOfferCarousel from 'components/OfferList/OfferListTiles/TourHeroOfferCarousel'
import { getMonthlyTourHomepageHeroImageId } from 'lib/tours/tourUtils'
import {
  BRAND_SIGNATURE_SERIES,
  isTourCampaignActive,
  TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  TOUR_CAMPAIGN_HOMEPAGE_OFFER_ID,
  TOUR_V2_SEARCH_HERO_IMAGE_ASIA_CAMPAIGN,
} from 'constants/tours'
import TourDealsTileCarousel from 'components/Tours/Deals/TourDealsTileCarousel'
import TourPageCategoriesCarousel from 'components/Tours/TourPageCategoriesCarousel'

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-bottom: ${rem(16)};
  padding-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(48)};
  }
`

const LayoutContainerSearchBarMobile = styled(LayoutContainer)`
  position: absolute;
  bottom: 0;
  transform:translateY(50%);
  z-index: 1;
`

interface MappedStateProps {
  banners: Array<App.Banner>
  listBanners: Array<App.ListBanner>
  urlFilters: App.OfferListFilters
  standaloneCruiseEnabled: boolean
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

interface CarouselDefinition {
  code: string
  title: React.ReactNode
  subtitle?: string
  locations?: Array<App.Place>
  operators?: Array<string>
}

const ttcOperatedCurrencies = new Set(['AUD', 'NZD', 'GBP', 'USD', 'CAD', 'EUR'])

function ToursPage(props: Props & MappedStateProps) {
  const {
    urlFilters,
    standaloneCruiseEnabled,
  } = props
  const toursVerticalName = useTourV2VerticalName()
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const isTourV2SearchEnabled = useTourV2SearchEnabled()
  const currentSiteTakeover = useCurrentSiteTakeover({ vertical: 'tours' })
  const promoSpendEarnEnabled = useAppSelector(state => isTourPromoSpendEarnEnabled(state))
  const showOperatorCarousel = ttcOperatedCurrencies.has(currentCurrency)
  const isTourCampaignEnabled = isTourCampaignActive(currentRegionCode, config.BRAND)
  const intl = useIntl()
  const toursDestinations = useMemo(() => ([
    intl.formatMessage({
      id: 'pVL9vCYG2MWOPtXwnljG',
      defaultMessage: 'Egypt',
      description: 'Example destination shown in the rotating mobile search placeholder on the tours page',
    }),
    intl.formatMessage({
      id: 'FhtqO9rmyD19ahicBApB',
      defaultMessage: 'New York',
      description: 'Example destination shown in the rotating mobile search placeholder on the tours page',
    }),
    intl.formatMessage({
      id: 'e3KWCS1z_EJ7IYTQo-UD',
      defaultMessage: 'Africa',
      description: 'Example destination shown in the rotating mobile search placeholder on the tours page',
    }),
    intl.formatMessage({
      id: 'eIr_OCh0tLrJUqbVa-Gs',
      defaultMessage: 'Europe',
      description: 'Example destination shown in the rotating mobile search placeholder on the tours page',
    }),
  ]), [intl])

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
    dispatch(fetchListBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return {
        ...urlFilters,
        destinations: TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
        operators: [BRAND_SIGNATURE_SERIES, LUXURY_ESCAPES],
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: 'vertical',
        limit: 250,
      }
    }
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      const isTourOnlyRegion = TOUR_ONLY_REGIONS.includes(currentRegionCode)
      const sortDefault = isTourOnlyRegion ? 'vertical' : 'vertical-tour'
      return {
        ...urlFilters,
        offerTypes: isTourOnlyRegion ? [OFFER_TYPE_TOUR_V2] : [OFFER_TYPE_TOUR_V2, OFFER_TYPE_TOUR, OFFER_TYPE_CRUISE],
        sortBy: urlFilters.sortBy ?? sortDefault,
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled, currentRegionCode, currentSiteTakeover])

  const headTitle = useMemo(() => {
    if (config.BRAND === LUXURY_ESCAPES) {
      return intl.formatMessage(
        {
          id: '1E8FmCwnx-SmgzJkMOCw',
          defaultMessage: 'Luxury {toursVerticalName}',
          description: 'Page title for luxury tours page on the tour offer detail page',
        },
        { toursVerticalName },
      )
    }
    return toursVerticalName
  }, [intl, toursVerticalName])

  const description = useMemo(() => {
    if (config.BRAND === LUXURY_ESCAPES) {
      return intl.formatMessage({
        id: '0YMQxJo1clEU4XaevO9O',
        defaultMessage: 'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.',
        description: 'Page description for luxury tours page in the cruise offer or booking section',
      })
    }
    return undefined
  }, [intl])

  const heroSearchBackgroundProps = useMemo<{ heroImageId: string, title: React.ReactNode, subtitle: string, gravity: TourHeroImageGravityType, tabletAspectRatio?: string }>(() => {
    const title = intl.formatMessage(
      {
        id: 'caZxcmw0HSTwPwDSrZfb',
        defaultMessage: '<i>Tour</i> the world with us',
        description: 'Hero title for tours page with italic Tour on the tour page',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
    const subtitle = intl.formatMessage({
      id: 'aJ5M0sVmoKTOPEuSu6ab',
      defaultMessage: 'Wander more with curated small-group adventures to destinations across the globe.',
      description: 'Hero banner subtitle for the tours listing page describing curated small-group adventures',
    })

    if (isTourCampaignEnabled) {
      return {
        heroImageId: TOUR_V2_SEARCH_HERO_IMAGE_ASIA_CAMPAIGN,
        title,
        subtitle,
        gravity: 'center',
        tabletAspectRatio: '4:1',
        aspectRatio: '2:1',
      }
    }

    if (currentSiteTakeover?.imageId) {
      return {
        heroImageId: currentSiteTakeover.imageId,
        title,
        subtitle,
        gravity: 'center',
        tabletAspectRatio: '4:1',
        aspectRatio: '2:1',
      }
    }
    const heroImage = getMonthlyTourHomepageHeroImageId()
    return {
      heroImageId: heroImage.id,
      title,
      subtitle,
      gravity: heroImage.gravity,
      aspectRatio: '2:1',
    }
  }, [currentSiteTakeover, intl, isTourCampaignEnabled])

  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()

  const onTourCarrouselAnalyticEvent = useCallback<OfferListEventHandler<{ category: string }>>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.productImpression && additionalData) {
      trackClientEvent({
        action: 'view',
        subject: `offerlist-tourspage-${additionalData.category}-carousel`,
        category: 'vertical_page',
        type: 'nonInteraction',
      })
    }
  }, [])

  const onTourTrendingDestinationsAnalyticEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.clientAction) {
      const { subject, action } = dispatchAction
      trackClientEvent({
        action,
        subject: `tour_trending_destinations_${subject}`,
        category: 'vertical_page',
        type: 'interaction',
      })
    }
  }, [])

  // Keep track of offer ids show from carousel to avoid duplication
  const [tourIdsMainCarousel, setTourIdsMainCarousel] = useState<Array<string>>([])
  const [tourIdsLuxExclusive, setTourIdsLuxExclusive] = useState<Array<string>>([])
  const [tourIdsLimitedTimeExclusive, setTourIdsLimitedTimeExclusive] = useState<Array<string>>([])
  const excludeIdsForTimeLimitedExclusiveCarousel = useMemo(() => [
    ...tourIdsMainCarousel,
    ...tourIdsLuxExclusive,
  ], [tourIdsMainCarousel, tourIdsLuxExclusive])
  const pinnedIdsForTimeLimitedExclusiveCarousel = useMemo(() => (
    isTourCampaignEnabled ? [TOUR_CAMPAIGN_HOMEPAGE_OFFER_ID] : undefined
  ), [isTourCampaignEnabled])

  const excludeIdsForTourStylesCarousel = useMemo(() => [
    ...excludeIdsForTimeLimitedExclusiveCarousel,
    ...tourIdsLimitedTimeExclusive,
  ], [excludeIdsForTimeLimitedExclusiveCarousel, tourIdsLimitedTimeExclusive])

  const searchPlaceholder = useMemo(() => intl.formatMessage({
    id: 'VWigIVqKeXaW55ZJpPym',
    defaultMessage: 'Search tours in',
    description: 'Placeholder text for tours search bar on the tour offer detail page',
  }), [intl])

  const exclusiveCarousel: CarouselDefinition = useMemo(() => ({
    code: 'luxexclusive',
    title: intl.formatMessage(
      {
        id: 'm7fZ4aGLsvbkNWNZxOSh',
        defaultMessage: 'Today’s top <i>exclusive</i> offers',
        description: 'Title for exclusive tours carousel with italic exclusive',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    ),
    locations: undefined,
  }), [intl])

  const limitedTimeExclusiveCarousel: CarouselDefinition = useMemo(() => ({
    code: 'limited_time_exclusive',
    title: intl.formatMessage(
      {
        id: 'VEbUmJQXhTLnaeAZ3uy1',
        defaultMessage: 'Explore our <i>limited time exclusive</i> tours',
        description: 'Title for limited time exclusive tours carousel with italic limited time exclusive',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    ),
    locations: undefined,
  }), [intl])

  const tourStylesCarousels: Array<CarouselDefinition> = useMemo(() => [
    {
      code: 'deluxe',
      title: intl.formatMessage(
        {
          id: 'JhK-TcNG1cCe5w0_Zp7G',
          defaultMessage: '<i>Deluxe</i> tours',
          description: 'Title for Deluxe tours carousel with italic Deluxe',
        },
        {
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      ),
      subtitle: intl.formatMessage({
        id: 'izkTqt7DTlka6KjTGWuk',
        defaultMessage: 'Discover the highlights and enjoy 4 & 4.5 star hotels with a maximum 24 travellers',
        description: 'Subtitle for Deluxe tours carousel on the tour offer detail page',
      }),
      locations: undefined,
    },
    {
      code: 'premium',
      title: intl.formatMessage(
        {
          id: 'MlVAcPCQEN0vDCNJkIPa',
          defaultMessage: '<i>Premium</i> tours',
          description: 'Title for Premium tours carousel with italic Premium',
        },
        {
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      ),
      subtitle: intl.formatMessage({
        id: 'gDFhydfKG3agABAJiMRs',
        defaultMessage: 'Elevated experiences with 5 star hotels and a maximum 16 travellers',
        description: 'Subtitle for Premium tours carousel on the tour offer detail page',
      }),
      locations: undefined,
    },
    {
      code: 'ultralux',
      title: intl.formatMessage(
        {
          id: 'x8v9dE0jxbMXXxg03ogD',
          defaultMessage: '<i>Ultra Lux</i> tours',
          description: 'Title for Ultra Lux tours carousel with italic Ultra Lux',
        },
        {
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      ),
      subtitle: intl.formatMessage({
        id: 'FsLLmEsq-vuyJImC6Y6J',
        defaultMessage: 'Only the best 5 star hotels & iconic stays with a maximum 12 travellers',
        description: 'Subtitle for Ultra Lux tours carousel on the tour offer detail page',
      }),
      locations: undefined,
    },
  ], [intl])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <div>
      <HeadData
        title={intl.formatMessage({
          id: '1JZY_6k1faBgicXwbXzK',
          defaultMessage: '{headTitle} - {configTitle} {currentRegionCode}',
          description: 'Browser page title for the tours listing page with region and category context',
        }, { headTitle, configTitle: config.title, currentRegionCode })}
        description={description}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        {promoSpendEarnEnabled && <TourSpendEarnPageBanner />}
        {isTourV2SearchEnabled && <TourSearchHeroContainer
          {...heroSearchBackgroundProps}
        >
          <TourSearchContainerPane>
            <TourSearchContainerPaneContent>
              <VerticalSpacer gap={16}>
                <TourV2SearchControls />
                <GlobalSearchLoyaltyBurnToggle compact skipBurnExperiment />
              </VerticalSpacer>
            </TourSearchContainerPaneContent>
          </TourSearchContainerPane>
          <LayoutContainerSearchBarMobile>
            <GlobalMobileSearchHeroBar placeholder={searchPlaceholder} placeholderTextList={toursDestinations} />
          </LayoutContainerSearchBarMobile>
        </TourSearchHeroContainer>}
        <>
          <OfferListEventsProvider
            onListEvent={onTourCarrouselAnalyticEvent}
            additionalData={{ category: 'topexclusive' }}
          >
            <TourHeroOfferCarousel filters={filters} onOffersShow={setTourIdsMainCarousel} />
          </OfferListEventsProvider>
          <OffersContainer>
            <Group direction="vertical" gap={56} tabletGap={80}>
              <TourDealsTileCarousel />
              <OfferListEventsProvider onListEvent={onTourTrendingDestinationsAnalyticEvent}>
                <TourV2TrendingDestinationsCarousel locations={getTourV2TrendingDestinations()} />
              </OfferListEventsProvider>
              <ErrorBoundary fallback={null}>
                <ToursPageHighIntentTours />
              </ErrorBoundary>
              <TourPageCategoriesCarousel />
              <OfferListEventsProvider
                onListEvent={onTourCarrouselAnalyticEvent}
                additionalData={{ category: 'topexclusive' }}
              >
                {
                  !!currentSiteTakeover &&
                  <TourPageSearchCarousel
                    offerIdsToExclude={tourIdsMainCarousel}
                    key={exclusiveCarousel.code}
                    title={exclusiveCarousel.title}
                    category={exclusiveCarousel.code}
                    places={exclusiveCarousel.locations}
                    sortType="vertical"
                    onOffersShow={setTourIdsLuxExclusive}
                  />
                }
                <TourPageSearchCarousel
                  offerIdsToExclude={excludeIdsForTimeLimitedExclusiveCarousel}
                  pinnedOfferIds={pinnedIdsForTimeLimitedExclusiveCarousel}
                  key={limitedTimeExclusiveCarousel.code}
                  title={limitedTimeExclusiveCarousel.title}
                  places={limitedTimeExclusiveCarousel.locations}
                  sortType="vertical"
                  flashOffer
                  onOffersShow={setTourIdsLimitedTimeExclusive}
                />
              </OfferListEventsProvider>
              {config.BRAND === LUXURY_ESCAPES && showOperatorCarousel && <OfferListEventsProvider
                onListEvent={onTourCarrouselAnalyticEvent}
                additionalData={{ category: 'topexclusive' }}
              >
                <LayoutContainer>
                  <TourUSPContainer />
                </LayoutContainer>
              </OfferListEventsProvider>}
              {config.TOUR_CATALOG_ENABLED && <LayoutContainer>
                <TourCatalogueBanner />
              </LayoutContainer>}
              {tourStylesCarousels.map(carousel =>
                <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData={{ category: 'topexclusive' }}
                  key={carousel.code}
                >
                  <TourPageSearchCarousel
                    offerIdsToExclude={excludeIdsForTourStylesCarousel}
                    title={carousel.title}
                    subtitle={carousel.subtitle}
                    style={carousel.code}
                    places={carousel.locations}
                    sortType="vertical"
                  />
                </OfferListEventsProvider>,
              )}
            </Group>
          </OffersContainer>
        </>
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    listBanners: state.content.listBanners?.toursAndCruises ?? EmptyArray,
    banners: state.content.banners.toursAndCruises,
    urlFilters: getUrlOfferListFilters(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
